/// <reference lib="dom" />
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import type { User, HhStatement, HhStatementSummary, HhInvoice, HhInvoiceItem } from 'shared/types.js';
import { api } from './api.js';
import { formatDate, currencyFormatter } from './utils.js';
import { HhInvoiceDetail } from './common.js';

const statementStatusOptions = [
    { value: 'new', label: 'New' },
    { value: 'viewed', label: 'Viewed' },
    { value: 'partially_cleared', label: 'Partially Cleared' },
    { value: 'cleared_with_issues', label: 'Cleared With Issues' },
    { value: 'fully_cleared', label: 'Fully Cleared' },
    { value: 'missing', label: 'Missing' },
] as const;
const defaultStatementStatuses = new Set<string>(statementStatusOptions.map(s => s.value));

const HhInvoiceDetailModal = ({
    invoiceNo,
    user,
    onClose
}: {
    invoiceNo: string;
    user: User;
    onClose: () => void;
}) => {
    const [invoice, setInvoice] = useState<HhInvoice | null>(null);
    const [items, setItems] = useState<HhInvoiceItem[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const canEditInvoices = user.permissions?.can_edit_invoices === 'true';

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const [fetchedInvoice, fetchedItems] = await Promise.all([
                api.get<HhInvoice>(`/api/hh-invoices/${invoiceNo}`),
                api.get<HhInvoiceItem[]>(`/api/hh-invoices/${invoiceNo}/items`)
            ]);
            setInvoice(fetchedInvoice);
            setItems(fetchedItems);
        } catch (error) {
            window.alert(`Failed to load invoice details for ${invoiceNo}. See console for more information.`);
            onClose();
        } finally {
            setIsLoading(false);
        }
    }, [invoiceNo, onClose]);
    useEffect(() => {
        fetchData();
    }, [fetchData]);
    useEffect(() => {
        document.body.classList.add('modal-open');
        return () => {
            document.body.classList.remove('modal-open');
        };
    }, []);
    const handleItemStatusChange = async (itemId: number, isCleared: boolean) => {
        try {
            await api.patch(`/api/hh-invoice-items/${itemId}/toggle-cleared`, { isCleared });
            await fetchData();
        } catch (error) {
            window.alert('Failed to update item status.');
        }
    };
    const handleUpdateNotes = async (notes: string) => {
        if (!invoice) return;
        try {
            await api.patch(`/api/hh-invoices/${invoice.invoice_no}/notes`, { notes });
            await fetchData();
        } catch (error) {
            throw error;
        }
    };
    const handleSaveChanges = async (invoiceNo: string, updatedItems: HhInvoiceItem[], invoiceData: HhInvoice) => {
        try {
            await api.put(`/api/hh-invoices/${invoiceNo}/items`, { items: updatedItems, invoice: invoiceData });
            await fetchData();
        } catch (error) {
            throw error;
        }
    };
    const handleClearAllItems = async () => {
        if (!invoice) return;
        try {
            await api.patch(`/api/hh-invoices/${invoice.invoice_no}/clear-all-items`);
            await fetchData();
        } catch (error) {
            window.alert('Failed to clear all items.');
        }
    };
    const handleClearInvoice = async () => {
        if (!invoice) return;
        try {
            await api.patch(`/api/hh-invoices/${invoice.invoice_no}/status`, { status: 'fully_cleared' });
            await fetchData();
        } catch (error) {
            window.alert('Failed to clear invoice.');
        }
    };
    const handleClearWithIssues = async () => {
        if (!invoice) return;
        try {
            await api.patch(`/api/hh-invoices/${invoice.invoice_no}/status`, { status: 'cleared_with_issues' });
            await fetchData();
        } catch (error) {
            window.alert('Failed to update invoice status.');
        }
    };
    return (
        <div className="modal-backdrop" onClick={onClose}>
            <div className="modal modal-invoice-detail" onClick={e => e.stopPropagation()}>
                <header className="modal-header">
                    <strong>HH Invoice Detail: {invoiceNo}</strong>
                    <button onClick={onClose} aria-label="Close modal">&times;</button>
                </header>
                <div className="modal-body">
                    {isLoading ? (
                        <div className="placeholder"><p>Loading invoice details...</p></div>
                    ) : invoice ? (
                        <HhInvoiceDetail
                            invoice={invoice}
                            items={items}
                            onClear={onClose}
                            onItemStatusChange={handleItemStatusChange}
                            onClearAllItems={handleClearAllItems}
                            onClearInvoice={handleClearInvoice}
                            onClearWithIssues={handleClearWithIssues}
                            onUpdateNotes={handleUpdateNotes}
                            onSaveChanges={handleSaveChanges}
                            canEditInvoices={canEditInvoices}
                        />
                    ) : (
                        <div className="placeholder"><p>Could not load invoice details.</p></div>
                    )}
                </div>
            </div>
        </div>
    );
};

const StatementListItem = ({ summary, isActive, onSelect }: { summary: HhStatementSummary, isActive: boolean, onSelect: () => void }) => (
    <li className={isActive ? 'active' : ''} onClick={onSelect}>
        <div className="invoice-list-item-header">
            <strong>{formatDate(summary.stmntDate)}</strong>
        </div>
        <div className="invoice-list-item-footer">
            <span>Statement</span>
            <span className={Number(summary.totalAmount) < 0 ? 'credit' : 'invoice'}>
                {currencyFormatter.format(Number(summary.totalAmount))}
            </span>
        </div>
    </li>
);

const StatementListContainer = ({
    summaries,
    activeDate,
    onSelectDate,
    isLoading
}: {
    summaries: HhStatementSummary[],
    activeDate: string | null,
    onSelectDate: (date: string | null) => void,
    isLoading: boolean
}) => {
    return (
        <div className="invoice-list-container">
            <div className="search-bar" style={{ justifyContent: 'center' }}>
                <h2 style={{ fontSize: '1.2rem', fontWeight: 600 }}>Monthly Statements</h2>
            </div>
            {isLoading && <div className="no-results">Loading...</div>}
            {!isLoading && summaries.length > 0 && (
                <ul className="invoice-list">
                    {summaries.map(summary => (
                        <StatementListItem
                            key={summary.stmntDate}
                            summary={summary}
                            isActive={activeDate === summary.stmntDate}
                            onSelect={() => onSelectDate(summary.stmntDate)}
                        />
                    ))}
                </ul>
            )}
            {!isLoading && summaries.length === 0 && (
                <p className="no-results">No statements found.</p>
            )}
        </div>
    );
};

const StatementDetailView = ({
    statementDate,
    details,
    onClear,
    onOpenModal,
    selectedStatuses,
    onStatusToggle,
    showCreditsOnly,
    onShowCreditsOnlyChange,
}: {
    statementDate: string,
    details: HhStatement[],
    onClear: () => void,
    onOpenModal: (invoiceNo: string) => void,
    selectedStatuses: Set<string>,
    onStatusToggle: (status: string) => void,
    showCreditsOnly: boolean,
    onShowCreditsOnlyChange: (enabled: boolean) => void,
}) => {
    const printRef = useRef<HTMLDivElement | null>(null);
    const statusMenuRef = useRef<HTMLDivElement | null>(null);
    const moreMenuRef = useRef<HTMLDivElement | null>(null);
    const [isStatusMenuOpen, setIsStatusMenuOpen] = useState(false);
    const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
    useEffect(() => {
        const handleOutsideClick = (event: MouseEvent) => {
            if (!(event.target instanceof Node)) return;
            if (statusMenuRef.current && !statusMenuRef.current.contains(event.target)) {
                setIsStatusMenuOpen(false);
            }
            if (moreMenuRef.current && !moreMenuRef.current.contains(event.target)) {
                setIsMoreMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, []);
    const selectedStatusLabels = statementStatusOptions
        .filter(status => selectedStatuses.has(status.value))
        .map(status => status.label);
    const filteredDetails = useMemo(() => {
        return details.filter(item => {
            if (showCreditsOnly && (Number(item.invoiceAmount) || 0) >= 0) {
                return false;
            }
            const statusKey = (item.status === null ? 'viewed' : item.status) || '';
            return selectedStatuses.has(statusKey);
        });
    }, [details, selectedStatuses, showCreditsOnly]);
    
    const totals = useMemo(() => {
        return filteredDetails.reduce((acc, item) => {
            acc.current += Number(item.currentAmount) || 0;
            acc.dating += Number(item.datingAmount) || 0;
            acc.pastDue += Number(item.pastDueAmount) || 0;
            acc.paid += Number(item.paidAmount) || 0;
            acc.invoiceAmount += Number(item.invoiceAmount) || 0;
            return acc;
        }, { current: 0, dating: 0, pastDue: 0, paid: 0, invoiceAmount: 0 });
    }, [filteredDetails]);

    const getStatusBadge = (status: HhStatement['status']) => {
        if (status === undefined) return null;
        const statusText = (status === null ? 'viewed' : status).replace(/_/g, ' ');
        const statusClass = `status-${(status === null ? 'viewed' : status).replace(/_/g, '-')}`;
        return (
            <span className={`outstanding-status-badge ${statusClass}`} style={{textTransform: 'capitalize'}}>
                {statusText}
            </span>
        );
    };
    const getTsDescription = (tsCode: string | null): string => {
        if (!tsCode) return '';
        const map: Record<string, string> = {
            '1': 'Warehouse',
            '2': 'Manufacturer',
            '3': 'Relay Shipments',
            '4': 'Promotions',
            '5': 'Appliance',
            '6': 'Building Supply',
            '7': 'Service',
            '8': 'Code 77',
            '9': 'Lumber',
            '10': 'Furniture',
            '11': 'Furniture',
            '12': 'Furniture',
            '13': 'Electronics'
        };
        return map[tsCode] || tsCode;
    };
    const handlePrintStatement = () => {
        if (!printRef.current) return;
        const printWindow = window.open('', '_blank', 'width=1100,height=800');
        if (!printWindow) return;
        const stylesheetLinks = Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
            .map(link => `<link rel="stylesheet" href="${(link as HTMLLinkElement).href}" />`)
            .join('');
        printWindow.document.write(`
            <!doctype html>
            <html>
                <head>
                    <meta charset="utf-8" />
                    <title>Statement ${statementDate}</title>
                    ${stylesheetLinks}
                    <style>
                        body { margin: 16px; background: #fff; color: #000; }
                        .back-button, .statement-controls-row { display: none !important; }
                        .invoice-detail-container.paper { box-shadow: none !important; border: none !important; margin: 0 !important; padding: 0 !important; }
                    </style>
                </head>
                <body>${printRef.current.outerHTML}</body>
            </html>
        `);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
            printWindow.print();
            printWindow.close();
        }, 150);
    };
    return (
        <div ref={printRef} className="invoice-detail-container paper" style={{ fontFamily: 'monospace' }}>
            <button onClick={onClear} className="back-button">&larr; Back to list</button>
            <header style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid #000' }}>
                <div>
                    <h2 style={{ fontWeight: 'bold', fontSize: '1.5rem', marginBottom: '0.5rem' }}>Home Hardware</h2>
                    <p>3265-9</p>
                    <p>METRO HOME BUILDING CENTRE</p>
                    <p>25 Riverside Drive</p>
                    <p>Charlottetown, Prince Edward Island</p>
                    <p>C1A 9R9</p>
                </div>
                <div style={{ textAlign: 'right' }}>
                    <h1 style={{ fontWeight: 'bold', fontSize: '1.5rem', marginBottom: '0.5rem' }}>Monthly Statement</h1>
                    <p>Home Hardware Stores Limited</p>
                    <p>34 Henry Street West</p>
                    <p>St. Jacobs, Ontario, Canada N0B 2N0</p>
                    <p>Phone (519) 664-2252 Ext. 6236</p>
                    <p>{new Date(statementDate).getUTCFullYear()}/{String(new Date(statementDate).getUTCMonth() + 1).padStart(2, '0')}</p>
                </div>
            </header>
            <div className="statement-controls-row" style={{ padding: '1rem 0', display: 'flex', alignItems: 'center', gap: '1rem', fontFamily: "'Inter', sans-serif", flexWrap: 'wrap' }}>
                <div className="tracker-filter-group filter-popover" ref={statusMenuRef}>
                    <label style={{ fontWeight: 600 }}>Filter by Status</label>
                    <button
                        type="button"
                        className="filter-popover-trigger"
                        onClick={() => {
                            setIsStatusMenuOpen(open => !open);
                            setIsMoreMenuOpen(false);
                        }}
                    >
                        <span>{selectedStatusLabels.length > 0 ? `${selectedStatusLabels.length} selected` : 'Select statuses'}</span>
                        <span className="filter-caret">{isStatusMenuOpen ? '▴' : '▾'}</span>
                    </button>
                    {isStatusMenuOpen && (
                        <div className="filter-popover-menu">
                            {statementStatusOptions.map(status => (
                                <label key={status.value} className="filter-menu-option">
                                    <input
                                        type="checkbox"
                                        checked={selectedStatuses.has(status.value)}
                                        onChange={() => onStatusToggle(status.value)}
                                    />
                                    <span>{status.label}</span>
                                </label>
                            ))}
                        </div>
                    )}
                </div>
                <div className="tracker-filter-group filter-popover" ref={moreMenuRef}>
                    <label style={{ fontWeight: 600 }}>More</label>
                    <button
                        type="button"
                        className="filter-popover-trigger"
                        onClick={() => {
                            setIsMoreMenuOpen(open => !open);
                            setIsStatusMenuOpen(false);
                        }}
                    >
                        <span>Filters</span>
                        {showCreditsOnly && <span className="filter-pill-count">1</span>}
                        <span className="filter-caret">{isMoreMenuOpen ? '▴' : '▾'}</span>
                    </button>
                    {isMoreMenuOpen && (
                        <div className="filter-popover-menu">
                            <label className="filter-menu-option">
                                <input
                                    type="checkbox"
                                    id="statement-credits-only"
                                    checked={showCreditsOnly}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => onShowCreditsOnlyChange(e.currentTarget.checked)}
                                />
                                <span>Show Credits Only</span>
                            </label>
                        </div>
                    )}
                </div>
                <button
                    type="button"
                    className="btn-secondary"
                    style={{ marginLeft: 'auto' }}
                    onClick={handlePrintStatement}
                >
                    Print Statement
                </button>
            </div>
            <table className="statement-table">
                <thead>
                    <tr>
                        <th style={{width: '10%'}}>Status</th>
                        <th style={{width: '5%'}}>Clerk</th>
                        <th style={{width: '8%'}}>Inv Nbr</th>
                        <th style={{width: '16%'}} className="statement-vendor-col">Vendor</th>
                        <th style={{width: '10%'}}>T/S</th>
                        <th style={{width: '11%'}}>Invoice Amount</th>
                        <th style={{width: '10%'}}>Invoice Date</th>
                        <th style={{width: '10%'}}>Due Date</th>
                        <th style={{width: '9%'}}>Current</th>
                        <th style={{width: '9%'}}>Dating</th>
                        <th style={{width: '9%'}}>Past Due</th>
                        <th style={{width: '10%'}}>Paid Amount</th>
                    </tr>
                </thead>
                <tbody>
                    {filteredDetails.map(item => (
                        <tr key={item.id} style={{ fontWeight: item.invoiceClass === '7' ? 'bold' : 'normal' }}>
                            <td>{getStatusBadge(item.status)}</td>
                            <td>{item.assigned_clerk}</td>
                            <td>
                                <button className="clickable-link" onClick={() => onOpenModal(item.invoice_no)}>
                                    {item.invoice_no}
                                </button>
                            </td>
                            <td className="statement-vendor-col">{item.vendor || ''}</td>
                            <td title={`Code: ${item.invoiceClass}`}>{getTsDescription(item.invoiceClass)}</td>
                            <td style={(Number(item.invoiceAmount) || 0) < 0 ? { color: 'var(--credit-color)' } : undefined}>
                                {item.invoiceAmount != null ? currencyFormatter.format(Number(item.invoiceAmount)) : ''}
                            </td>
                            <td>{formatDate(item.invoiceDate)}</td>
                            <td>{formatDate(item.dueDate)}</td>
                            <td>{item.currentAmount != null ? currencyFormatter.format(Number(item.currentAmount)) : ''}</td>
                            <td>{item.datingAmount != null ? currencyFormatter.format(Number(item.datingAmount)) : ''}</td>
                            <td>{item.pastDueAmount != null ? currencyFormatter.format(Number(item.pastDueAmount)) : ''}</td>
                            <td>{item.paidAmount != null ? currencyFormatter.format(Number(item.paidAmount)) : ''}</td>
                        </tr>
                    ))}
                </tbody>
                 <tfoot>
                    <tr>
                        <td colSpan={5}>TOTALS</td>
                        <td>{currencyFormatter.format(totals.invoiceAmount)}</td>
                        <td colSpan={2}></td>
                        <td>{currencyFormatter.format(totals.current)}</td>
                        <td>{currencyFormatter.format(totals.dating)}</td>
                        <td>{currencyFormatter.format(totals.pastDue)}</td>
                        <td>{currencyFormatter.format(totals.paid)}</td>
                    </tr>
                </tfoot>
            </table>
        </div>
    );
};

export const HhStatements = ({ user }: { user: User }) => {
    const [summaries, setSummaries] = useState<HhStatementSummary[]>([]);
    const [activeDate, setActiveDate] = useState<string | null>(null);
    const [details, setDetails] = useState<HhStatement[]>([]);
    const [isLoadingList, setIsLoadingList] = useState(true);
    const [isLoadingDetails, setIsLoadingDetails] = useState(false);
    const [modalInvoiceNo, setModalInvoiceNo] = useState<string | null>(null);
    const [selectedStatuses, setSelectedStatuses] = useState<Set<string>>(new Set(defaultStatementStatuses));
    const [showCreditsOnly, setShowCreditsOnly] = useState(false);
    const handleStatusToggle = (status: string) => {
        setSelectedStatuses(prev => {
            const newSet = new Set(prev);
            if (status === 'new') {
                if (newSet.has('new')) {
                    newSet.delete('new');
                    newSet.delete('viewed');
                } else {
                    newSet.add('new');
                    newSet.add('viewed');
                }
            } else {
                if (newSet.has(status)) {
                    newSet.delete(status);
                } else {
                    newSet.add(status);
                }
            }
            return newSet;
        });
    };
    
    const handleSelectDate = useCallback(async (date: string | null) => {
        setActiveDate(date);
        setSelectedStatuses(new Set(defaultStatementStatuses));
        setShowCreditsOnly(false);
        if (date) {
            setIsLoadingDetails(true);
            try {
                const fetchedDetails = await api.get<HhStatement[]>(`/api/statements/${date}`);
                setDetails(fetchedDetails);
            } catch (error) {
                setDetails([]);
            } finally {
                setIsLoadingDetails(false);
            }
        } else {
            setDetails([]);
        }
    }, []);
    useEffect(() => {
        const fetchSummaries = async () => {
            setIsLoadingList(true);
            try {
                const data = await api.get<HhStatementSummary[]>('/api/statements');
                setSummaries(data);
                if (data.length > 0 && !activeDate) {
                    handleSelectDate(data[0].stmntDate);
                }
            } catch (error) {
                // No-op
            } finally {
                setIsLoadingList(false);
            }
        };
        fetchSummaries();
    }, [handleSelectDate, activeDate]);
    const handleOpenModal = (invoiceNo: string) => {
        setModalInvoiceNo(invoiceNo);
    };
    const handleCloseModal = () => {
        setModalInvoiceNo(null);
        if (activeDate) {
            handleSelectDate(activeDate);
        }
    };
    return (
        <div className={`main-content ${activeDate ? 'detail-view-active' : ''}`}>
            <StatementListContainer
                summaries={summaries}
                activeDate={activeDate}
                onSelectDate={handleSelectDate}
                isLoading={isLoadingList}
            />
            <div className="invoice-detail-view">
                {activeDate && !isLoadingDetails && (
                    <StatementDetailView
                        statementDate={activeDate}
                        details={details}
                        onClear={() => handleSelectDate(null)}
                        onOpenModal={handleOpenModal}
                        selectedStatuses={selectedStatuses}
                        onStatusToggle={handleStatusToggle}
                        showCreditsOnly={showCreditsOnly}
                        onShowCreditsOnlyChange={setShowCreditsOnly}
                    />
                )}
                {!activeDate && !isLoadingList && (
                    <div className="placeholder">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M20 6h-4V4c0-1.1-.9-2-2-2h-4c-1.1 0-2 .9-2 2v2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zM12 4h4v2h-4V4z"></path></svg>
                        <p>Select a statement to view its details</p>
                    </div>
                )}
                {(isLoadingDetails || (isLoadingList && !activeDate)) && (
                    <div className="placeholder"><p>Loading details...</p></div>
                )}
            </div>
            {modalInvoiceNo && (
                <HhInvoiceDetailModal
                    invoiceNo={modalInvoiceNo}
                    user={user}
                    onClose={handleCloseModal}
                />
            )}
        </div>
    );
};
